import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, Clock, Plus, LogOut, AlertTriangle, CheckCircle2, UserCheck, LogOutIcon, Zap } from "lucide-react";
import { Button, Card, Badge, Spinner, Modal, Textarea } from "../shared/ui";
import DateTimePicker from "./DateTimePicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const SiteCrewPanel = ({ jobId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Clock-on modal
  const [showClockOnModal, setShowClockOnModal] = useState(false);
  const [clockOnTarget, setClockOnTarget] = useState(null);
  const [clockOnForm, setClockOnForm] = useState({ start_time: '', notes: '' });

  // Clock-off modal
  const [showClockOffModal, setShowClockOffModal] = useState(false);
  const [clockOffTarget, setClockOffTarget] = useState(null);
  const [clockOffForm, setClockOffForm] = useState({ end_time: '', notes: '' });

  // Bulk clock-off modal
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkForm, setBulkForm] = useState({ end_time: '', notes: '' });
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  const fetchCrew = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const res = await axios.get(`${API}/jobs/${jobId}/site-crew`);
      setData(res.data);
    } catch (e) {
      if (e.response?.status !== 403) {
        console.error(e);
      }
      setData({ error: e.response?.data?.detail || 'Unable to load site crew', clocked_on: [], assigned: [], can_manage: false });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [jobId]);

  useEffect(() => { fetchCrew(); }, [fetchCrew]);
  useEffect(() => {
    // Auto-refresh every 60s so the list stays live
    const i = setInterval(() => fetchCrew(true), 60000);
    return () => clearInterval(i);
  }, [fetchCrew]);

  const openClockOn = (user) => {
    setClockOnTarget(user);
    setClockOnForm({ start_time: new Date().toISOString(), notes: '' });
    setShowClockOnModal(true);
  };

  const openClockOff = (crew) => {
    setClockOffTarget(crew);
    setClockOffForm({ end_time: new Date().toISOString(), notes: '' });
    setShowClockOffModal(true);
  };

  const openBulkClockOff = () => {
    setBulkForm({ end_time: new Date().toISOString(), notes: '' });
    setShowBulkModal(true);
  };

  const submitClockOn = async () => {
    if (!clockOnTarget) return;
    try {
      const res = await axios.post(`${API}/jobs/${jobId}/site-crew/clock-on`, {
        user_id: clockOnTarget.id,
        start_time: clockOnForm.start_time || undefined,
        notes: clockOnForm.notes || null,
      });
      const pending = res.data?.pending_forms?.length || 0;
      const paired = res.data?.paired_clocked_on?.length || 0;
      toast.success(
        `${clockOnTarget.name} clocked on — notified` +
        (paired ? `, paired ${paired} dogman` : '') +
        (pending ? `, ${pending} form${pending === 1 ? '' : 's'} to sign` : '')
      );
      setShowClockOnModal(false);
      fetchCrew(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't clock them on");
    }
  };

  const submitClockOff = async () => {
    if (!clockOffTarget) return;
    try {
      const res = await axios.post(`${API}/jobs/${jobId}/site-crew/clock-off`, {
        user_id: clockOffTarget.user_id,
        end_time: clockOffForm.end_time || undefined,
        notes: clockOffForm.notes || null,
      });
      const paired = res.data?.paired_clocked_off?.length || 0;
      toast.success(
        `${clockOffTarget.user_name} clocked off (${res.data?.total_hours || '?'}h)` +
        (paired ? ` — paired ${paired} crew also closed` : '')
      );
      setShowClockOffModal(false);
      fetchCrew(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't clock them off");
    }
  };

  const submitBulkClockOff = async () => {
    setBulkSubmitting(true);
    try {
      const res = await axios.post(`${API}/jobs/${jobId}/site-crew/bulk-clock-off`, {
        end_time: bulkForm.end_time || undefined,
        notes: bulkForm.notes || null,
      });
      toast.success(`Knocked off — ${res.data.closed_count} crew clocked out` +
        (res.data.skipped_count ? ` (${res.data.skipped_count} skipped)` : ''));
      setShowBulkModal(false);
      fetchCrew(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't bulk clock off");
    } finally {
      setBulkSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8" data-testid="site-crew-loading">
        <Spinner />
      </div>
    );
  }

  if (data?.error && !data.can_manage && data.clocked_on.length === 0) {
    return (
      <Card className="bg-amber-50 border-amber-200" data-testid="site-crew-restricted">
        <div className="flex items-center gap-2 text-amber-700 text-sm">
          <AlertTriangle className="w-4 h-4" />
          <span>{data.error || 'Site crew view is restricted.'}</span>
        </div>
      </Card>
    );
  }

  const clockedOn = data?.clocked_on || [];
  const assigned = data?.assigned || [];
  const notYetClockedOn = assigned.filter(a => !a.is_clocked_on);
  const canManage = !!data?.can_manage;

  return (
    <div className="space-y-4" data-testid="site-crew-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-orange-500" />
          <h3 className="text-sm font-bold uppercase tracking-wide">Site Crew</h3>
          <Badge variant={clockedOn.length > 0 ? 'success' : 'default'} data-testid="site-crew-count-badge">
            {clockedOn.length} on site
          </Badge>
          {refreshing && <Spinner size="sm" />}
        </div>
        <div className="flex items-center gap-2">
          {canManage && clockedOn.length > 1 && (
            <Button
              size="sm"
              variant="destructive"
              onClick={openBulkClockOff}
              data-testid="site-crew-bulk-clock-off-btn"
              title="Clock off every crew member on this job at one knock-off time"
            >
              <Zap className="w-3.5 h-3.5 mr-1" /> Knock off all
            </Button>
          )}
          {!canManage && (
            <span className="text-[10px] uppercase font-bold text-slate-400">View only</span>
          )}
        </div>
      </div>

      {/* Currently clocked on */}
      {clockedOn.length === 0 ? (
        <Card className="text-center py-6 text-sm text-slate-500" data-testid="site-crew-empty">
          No one is currently clocked on to this job.
        </Card>
      ) : (
        <div className="space-y-2">
          {clockedOn.map((c) => (
            <div
              key={c.entry_id}
              className="flex items-center justify-between gap-3 p-3 rounded-lg border bg-green-50/50 border-green-200"
              data-testid={`clocked-on-row-${c.user_id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <UserCheck className="w-4 h-4 text-green-600" />
                  <span className="font-bold truncate">{c.user_name}</span>
                  {c.role && <span className="text-[10px] uppercase font-bold text-slate-500">{c.role}</span>}
                  {c.clocked_by_supervisor && (
                    <span
                      className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 border border-purple-300"
                      title={`Clocked on by ${c.clocked_by_user_name || 'supervisor'}`}
                    >
                      SUPERVISOR ON
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    in at {fmtTime(c.clock_in)}
                  </span>
                  {c.hours_so_far != null && (
                    <span className="font-semibold text-slate-700">
                      {c.hours_so_far}h so far
                    </span>
                  )}
                  {c.linked_dogman_id && <Badge variant="default">paired</Badge>}
                </div>
              </div>
              {canManage && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openClockOff(c)}
                  data-testid={`clock-off-btn-${c.user_id}`}
                >
                  <LogOut className="w-3.5 h-3.5 mr-1" /> Clock off
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Assigned but not clocked on — only shown to managers */}
      {canManage && notYetClockedOn.length > 0 && (
        <div className="pt-2">
          <p className="text-[11px] uppercase font-bold text-slate-500 mb-2">
            Assigned crew not yet clocked on ({notYetClockedOn.length})
          </p>
          <div className="space-y-1.5">
            {notYetClockedOn.map((u) => (
              <div
                key={u.id}
                className="flex items-center justify-between gap-3 p-2 rounded-md border bg-slate-50"
                data-testid={`assigned-off-row-${u.id}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Users className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-sm font-semibold truncate">{u.name}</span>
                  {u.role && <span className="text-[10px] uppercase text-slate-500">{u.role}</span>}
                </div>
                <Button
                  size="sm"
                  onClick={() => openClockOn(u)}
                  data-testid={`clock-on-btn-${u.id}`}
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Clock on
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {!canManage && clockedOn.length > 0 && (
        <p className="text-[11px] italic text-slate-400 text-center">
          Only assigned supervisors or admin can clock others on/off for this job.
        </p>
      )}

      {/* Clock-on modal */}
      {showClockOnModal && clockOnTarget && (
        <Modal isOpen={true} onClose={() => setShowClockOnModal(false)} title={`Clock on ${clockOnTarget.name}`}>
          <div className="space-y-4" data-testid="site-crew-clock-on-modal">
            <p className="text-sm text-slate-600">
              They'll get a notification and be prompted to sign any forms they've still got pending for today.
            </p>
            <DateTimePicker
              label="Start time"
              value={clockOnForm.start_time}
              onChange={(iso) => setClockOnForm({ ...clockOnForm, start_time: iso })}
              maxDate={new Date()}
              testId="site-crew-clock-on-start"
            />
            <p className="text-xs text-slate-500 -mt-2">
              Leave as "now" to clock them on immediately, or backdate if they forgot this morning.
            </p>
            <Textarea
              label="Notes (optional)"
              value={clockOnForm.notes}
              onChange={(e) => setClockOnForm({ ...clockOnForm, notes: e.target.value })}
              placeholder="e.g. Forgot to clock on at 7am"
              data-testid="site-crew-clock-on-notes"
            />
            <div className="flex gap-2">
              <Button onClick={submitClockOn} data-testid="site-crew-submit-clock-on">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Clock on
              </Button>
              <Button variant="ghost" onClick={() => setShowClockOnModal(false)}>Cancel</Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Clock-off modal */}
      {showClockOffModal && clockOffTarget && (
        <Modal isOpen={true} onClose={() => setShowClockOffModal(false)} title={`Clock off ${clockOffTarget.user_name}`}>
          <div className="space-y-4" data-testid="site-crew-clock-off-modal">
            <div className="rounded-lg bg-slate-50 p-3 text-sm">
              <div>In at <strong>{fmtTime(clockOffTarget.clock_in)}</strong></div>
              {clockOffTarget.hours_so_far != null && <div>Hours so far: <strong>{clockOffTarget.hours_so_far}h</strong></div>}
              {clockOffTarget.linked_dogman_id && <div className="text-purple-600 font-semibold">⚠ Paired crew — will also be clocked off</div>}
            </div>
            <DateTimePicker
              label="End time"
              value={clockOffForm.end_time}
              onChange={(iso) => setClockOffForm({ ...clockOffForm, end_time: iso })}
              minDate={clockOffTarget?.clock_in ? new Date(clockOffTarget.clock_in) : undefined}
              testId="site-crew-clock-off-end"
            />
            <Textarea
              label="Notes (optional)"
              value={clockOffForm.notes}
              onChange={(e) => setClockOffForm({ ...clockOffForm, notes: e.target.value })}
              placeholder="e.g. Worker left at 4:30pm"
              data-testid="site-crew-clock-off-notes"
            />
            <div className="flex gap-2">
              <Button onClick={submitClockOff} data-testid="site-crew-submit-clock-off">
                <LogOut className="w-4 h-4 mr-1" /> Clock off
              </Button>
              <Button variant="ghost" onClick={() => setShowClockOffModal(false)}>Cancel</Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Bulk clock-off modal */}
      {showBulkModal && (
        <Modal isOpen={true} onClose={() => !bulkSubmitting && setShowBulkModal(false)} title="Knock off entire site crew">
          <div className="space-y-4" data-testid="site-crew-bulk-modal">
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm">
              <div className="flex items-center gap-2 font-bold text-red-700 mb-1">
                <LogOutIcon className="w-4 h-4" /> Clocking off {clockedOn.length} crew member{clockedOn.length === 1 ? '' : 's'}
              </div>
              <p className="text-xs text-red-800">
                Every worker currently on this job will be clocked off at the time you pick. They'll all get a notification and a reminder to sign any outstanding forms.
              </p>
            </div>
            <DateTimePicker
              label="Knock-off time"
              value={bulkForm.end_time}
              onChange={(iso) => setBulkForm({ ...bulkForm, end_time: iso })}
              testId="site-crew-bulk-end"
            />
            <Textarea
              label="Notes (optional)"
              value={bulkForm.notes}
              onChange={(e) => setBulkForm({ ...bulkForm, notes: e.target.value })}
              placeholder="e.g. Site closed at 4pm — all crew off"
              data-testid="site-crew-bulk-notes"
            />
            <div className="flex gap-2">
              <Button
                variant="destructive"
                onClick={submitBulkClockOff}
                disabled={bulkSubmitting}
                data-testid="site-crew-submit-bulk-clock-off"
              >
                <Zap className="w-4 h-4 mr-1" />
                {bulkSubmitting ? 'Knocking off…' : `Knock off all ${clockedOn.length}`}
              </Button>
              <Button variant="ghost" onClick={() => setShowBulkModal(false)} disabled={bulkSubmitting}>Cancel</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default SiteCrewPanel;
